/**
 * @file NotificationLogPage.tsx
 * @description Страница «Уведомления» (phase19): вкладка «Журнал» —
 * отправленные/пропущенные уведомления магазина; вкладка «Инвайты» —
 * все инвайты в ЛК со статусами (ожидает / активирован / истёк / замещён).
 *
 * RBAC: журнал — SHOP_MANAGE_SETTINGS, инвайты — SHOP_MANAGE_CUSTOMERS
 * (при отсутствии права API вернёт 403 — показываем сообщение).
 * Mobile-first: таблицы в overflow-x контейнере, фильтры кнопками.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { ApiError } from "../../../lib/api-client";
import { NotificationLogTable } from "./sections/NotificationLogTable";
import { InvitationsTable } from "./sections/InvitationsTable";

type Tab = "log" | "invitations";

export function NotificationLogPage() {
  const [tab, setTab] = useState<Tab>("log");

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Настройки · Уведомления</div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">Уведомления</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Журнал отправленных сообщений клиентам и статусы инвайтов в личный
          кабинет. Получатели показаны в маскированном виде.
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          variant={tab === "log" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("log")}
        >
          Журнал
        </Button>
        <Button
          variant={tab === "invitations" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("invitations")}
        >
          Инвайты в ЛК
        </Button>
      </div>

      {tab === "log" ? <NotificationLogTable /> : <InvitationsTable />}
    </div>
  );
}

/** Общий блок загрузки/ошибки для вкладок. */
export function TabStateBlock({
  loading,
  error,
}: {
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-on-surface-variant" />
      </div>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  return null;
}

/** Пустое состояние вкладки. */
export function TabEmpty({ text }: { text: string }) {
  return (
    <EditorialCard>
      <EditorialCard.Body className="py-12 text-center text-on-surface-variant">
        {text}
      </EditorialCard.Body>
    </EditorialCard>
  );
}

/** Форматирование даты-времени ru-RU. */
export function formatLogDate(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Единый разбор ошибок API для вкладок. */
export function tabErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) {
    return "Недостаточно прав для просмотра этого раздела.";
  }
  return err instanceof ApiError ? err.message : "Не удалось загрузить данные.";
}
